/* eslint-disable camelcase */
import React, { createContext, useContext } from 'react';

import { Widget, WidgetContextType } from '@/api/rest/resources/types/widget';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { useProjectFact } from '@/pages/shared/hooks/useProjectFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';

const useWidgetDataForPlot = (widget: Widget) => {
  const { widget_format = {} } = widget || {};
  const { factName = '' } = widget_format;
  const facts = usePlotFacts([factName]);
  return getFactFromFactArray(facts, factName)?.value;
};

const useWidgetDataForProject = (widget: Widget) => {
  const { widget_format = {} } = widget || {};
  const { factName = '' } = widget_format;
  return useProjectFact(factName)?.value;
};

const useWidgetDataForOrganizationReport = (widget: Widget) => {
  const { widget_format = {} } = widget || {};
  const { factName = '' } = widget_format;
  return useOrganizationIndicatorReportingFact(factName);
};

const useWidgetDataForOrganizationGraph = (widget: Widget) => {
  const { widget_format = {} } = widget || {};
  const { factName = '' } = widget_format;
  return useOrganizationIndicatorGraphFact(factName);
};

export type WidgetContextValue = {
  widget: Widget;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
};

export const WidgetDataContext = createContext<WidgetContextValue | undefined>(undefined);

export const useWidgetDataContext = () => useContext(WidgetDataContext);

export const WidgetContext = ({ widget, children }: { widget: Widget; children?: React.ReactNode }) => {
  const { widget_format = {} } = widget || {};

  const { context, data } = widget_format;

  switch (context) {
    case WidgetContextType.plot:
      return <WidgetContextForPlot widget={widget}>{children}</WidgetContextForPlot>;
    case WidgetContextType.project:
      return <WidgetContextForProject widget={widget}>{children}</WidgetContextForProject>;
    case WidgetContextType.organization_report:
      return <WidgetContextForOrganizationReport widget={widget}>{children}</WidgetContextForOrganizationReport>;
    case WidgetContextType.organization_graph:
      return <WidgetContextForOrganizationGraph widget={widget}>{children}</WidgetContextForOrganizationGraph>;
    default:
      return <WidgetDataContext.Provider value={{ widget, data }}>{children}</WidgetDataContext.Provider>;
  }
};

export const WidgetContextForPlot = ({ widget, children }: { widget: Widget; children?: React.ReactNode }) => {
  const data = useWidgetDataForPlot(widget);
  return <WidgetDataContext.Provider value={{ widget, data }}>{children}</WidgetDataContext.Provider>;
};

export const WidgetContextForProject = ({ widget, children }: { widget: Widget; children?: React.ReactNode }) => {
  const data = useWidgetDataForProject(widget);
  return <WidgetDataContext.Provider value={{ widget, data }}>{children}</WidgetDataContext.Provider>;
};

export const WidgetContextForOrganizationReport = ({
  widget,
  children,
}: {
  widget: Widget;
  children?: React.ReactNode;
}) => {
  const data = useWidgetDataForOrganizationReport(widget);
  return <WidgetDataContext.Provider value={{ widget, data }}>{children}</WidgetDataContext.Provider>;
};

export const WidgetContextForOrganizationGraph = ({
  widget,
  children,
}: {
  widget: Widget;
  children?: React.ReactNode;
}) => {
  const data = useWidgetDataForOrganizationGraph(widget);
  return <WidgetDataContext.Provider value={{ widget, data }}>{children}</WidgetDataContext.Provider>;
};
